import React, { useEffect, useRef, useState } from "react";
import { FormikProps } from "formik";

//Style
import Styles from "./Otp.module.scss";

//Components
import { LoginFormFieldsTypes, loginFormFieldNames } from "../../common/contant";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import Typography from "@vahak/core-ui/dist/components/Typography";

//Assets
import WhatsappIcon from "@vahak/core/dist/icons/WhatsappGreen.svg";
import SmsIcon from "@vahak/core/dist/icons/sms.svg";
import EditIconBlue from "@vahak/core/dist/icons/edit_icon_blue.svg";

import { GA4EventNames } from "@vahak/core/dist/constants/event-names";
import { IDs } from "@vahak/core/dist/constants/automation";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import OTPInput from "@vahak/core-ui/dist/components/OTPInput";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";
import { GenericObject } from "@vahak/core-ui/dist/types/common";
import { timer } from "@vahak/core/dist/methods/timer";
import { OnClickResendOTPParams } from "@vahak/core/dist/components/auth/useAuth";
import TextCTA from "@vahak/core-ui/dist/components/TextCTA";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { LOGIN_OTP_LENGTH, useSmsOtpAutofill } from "../../hooks/auth";

interface OtpFieldProps {
    formik: FormikProps<LoginFormFieldsTypes>;
    onClickResendOtp: (p: OnClickResendOTPParams) => void;
    onClickEditNumber?: () => void;
}

const formatToString = (n: number) => {
    return n.toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false
    });
};

export const getTimeRemaining = (time: number) => {
    const minutes = formatToString(Math.floor((time / 60) % 60));
    const seconds = formatToString(Math.floor(time % 60));
    return {
        seconds,
        minutes
    };
};

const OtpField = ({ formik, onClickResendOtp, onClickEditNumber }: OtpFieldProps) => {
    const bufferTime = 30;
    const otpLength = LOGIN_OTP_LENGTH;
    const [timeRemaining, setTimeRemaining] = useState(bufferTime);
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const otpWrapperRef = useRef<HTMLDivElement>(null);

    const otpDigitCount = String(formik.values.otp ?? "").replace(/\D/g, "").length;
    const showOtpError = Boolean(formik.errors.otp && otpDigitCount === otpLength);

    const clearOtpError = () => {
        if (formik.errors.otp) {
            formik.setFieldError(loginFormFieldNames.otp, undefined);
        }
    };

    useEffect(() => {
        const inputs = otpWrapperRef.current?.querySelectorAll("input");
        if (!inputs?.length) {
            return;
        }

        inputs.forEach((input) => input.addEventListener("focus", clearOtpError));
        return () => inputs.forEach((input) => input.removeEventListener("focus", clearOtpError));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.errors.otp]);

    const onChangeOTP = (str: string) => {
        clearOtpError();
        formik.setFieldValue(loginFormFieldNames.otp, str.trim());
        if (str.trim().length === otpLength) {
            setTimeout(() => {
                formik.handleSubmit();
            });
        }
    };

    const { startListening: startSmsOtpAutofill } = useSmsOtpAutofill({
        onOtp: (code) => {
            otpWrapperRef?.current?.querySelector("input")?.blur();
            formik.setFieldValue(loginFormFieldNames.otp, code);
            formik.handleSubmit();
        }
    });

    const successCallback = () => {
        setIsResendingOtp(false);
        timer(bufferTime, (t) => {
            setTimeRemaining(t);
        });
    };

    const { sendGAandMoEngageEvent } = useEventTrackerService();
    const handleEventTracking = (name: string, otherData?: GenericObject) => {
        const data = {
            source_screen: "phone_number_registration",
            phone_number: formik.values.phoneNumber,
            ...otherData
        };

        sendGAandMoEngageEvent({
            name,
            data
        });
    };

    const handleSmsResend = () => {
        handleEventTracking(GA4EventNames.LOGIN_OR_SIGNUP_MODAL["otp_via_message"]);

        clearOtpError();
        setIsResendingOtp(true);
        startSmsOtpAutofill();
        onClickResendOtp?.({
            onSuccessCallback: successCallback
        });
    };

    const handleWAResend = () => {
        handleEventTracking(GA4EventNames.LOGIN_OR_SIGNUP_MODAL["otp_via_whatsapp"]);

        clearOtpError();
        setIsResendingOtp(true);
        onClickResendOtp?.({
            isWhatsApp: true,
            onSuccessCallback: successCallback
        });
    };

    useEffect(() => {
        const inputs = otpWrapperRef.current?.querySelectorAll("input");
        inputs?.forEach((input) => input.setAttribute("autocomplete", "one-time-code"));
    }, []);

    useEffect(() => {
        const timerId = timer(bufferTime, (t) => {
            setTimeRemaining(t);
        });
        return () => clearInterval(timerId);
    }, []);

    const { minutes, seconds } = getTimeRemaining(timeRemaining);

    const disableResendAction = timeRemaining > 0 || isResendingOtp;

    const isMobileScreen = useMediaQuery({ queryType: "mobile" });

    return (
        <Flex flexDirection="column" className={Styles.main} gap={isMobileScreen ? 20 : 15}>
            <Typography color={COLORS.GREY_500} weight="medium" align="center">
                We’ve sent an SMS with an OTP to your
            </Typography>
            <Flex gap={10} alignItems="center" justifyContent="center">
                <Typography color={COLORS.GREY_500} weight="medium" align="center">
                    phone{" "}
                    <Typography color={COLORS.BLACK} weight="medium">
                        +91 {formik.values.phoneNumber}
                    </Typography>
                </Typography>
                <TextCTA
                    size="m"
                    weight="regular"
                    underline
                    onClick={onClickEditNumber}
                    iconNode={<EditIconBlue />}
                    id={IDs.auth.editPhoneNumber}
                >
                    Edit
                </TextCTA>
            </Flex>
            <OTPInput
                id={IDs.auth.otp}
                ref={otpWrapperRef}
                length={otpLength}
                onChange={onChangeOTP}
                value={String(formik.values.otp ?? "")}
                errorMsg={showOtpError ? String(formik.errors.otp) : ""}
            />

            <Flex alignItems="center" flexDirection="column" justifyContent="center" gap={15}>
                {disableResendAction ? (
                    <Flex gap={5}>
                        <Typography size="sm" color={COLORS.GREY_500}>
                            Resend OTP in
                        </Typography>
                        <Typography size="sm" weight="semibold">
                            {" "}
                            {minutes}:{seconds}s
                        </Typography>
                    </Flex>
                ) : (
                    <Typography size="sm" color={COLORS.GREY_500}>
                        Resend OTP to
                    </Typography>
                )}
                {!disableResendAction ? (
                    <Flex alignItems="center" gap={15}>
                        <TextCTA
                            size="sm"
                            weight="regular"
                            underline
                            onClick={handleSmsResend}
                            id={IDs.auth.resendBtnSms}
                            iconNode={<SmsIcon />}
                        >
                            OTP via SMS
                        </TextCTA>
                        <TextCTA
                            size="sm"
                            weight="regular"
                            underline
                            onClick={handleWAResend}
                            id={IDs.auth.resendBtnWa}
                            iconNode={<WhatsappIcon />}
                        >
                            OTP via WhatsApp
                        </TextCTA>
                    </Flex>
                ) : (
                    <></>
                )}
            </Flex>
        </Flex>
    );
};

export default OtpField;
